import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useGetCapabilities } from './backend/shared'
import { capabilityCaption } from '../utils/access'
import { nextDegradedDelay } from './degradedRetry'

export type CapabilityStatus = 'ok' | 'degraded'

export type Capabilities = {
  canView: boolean
  canEditFleet: boolean
  canEditDrivers: boolean
  canEditOwners: boolean
  // Owner statements, payouts and retention: an owner's financial position.
  // Read from cockpit_access.can_view_finance, which the ported
  // resolveCapabilities ignored until 22 August 2026.
  canViewFinance: boolean
  canPushXero: boolean
  isAdmin: boolean
  // Marketing, added 24 August 2026. Three flags, not one: writing content,
  // signing it off, and being the person design requests land on are three
  // different jobs, and Turf holds the first and third while Luyanda holds the
  // second.
  canEditMarketing: boolean
  canApproveContent: boolean
  isDesigner: boolean
  // Xolile's Driver Pipeline comment feature, migration 0391 (renamed from 0344). Does not
  // imply canEditDrivers or any interview action/viewer permission.
  canCommentOnPipeline: boolean
  personId: string | null
  status: CapabilityStatus
}

type CapabilitiesContextValue = {
  capabilities: Capabilities | null
  loading: boolean
  canView: boolean
  canEditFleet: boolean
  canEditDrivers: boolean
  canEditOwners: boolean
  canViewFinance: boolean
  canPushXero: boolean
  isAdmin: boolean
  canEditMarketing: boolean
  canApproveContent: boolean
  isDesigner: boolean
  canCommentOnPipeline: boolean
  degraded: boolean
  /**
   * The line under a page title. See capabilityCaption for why it is derived
   * here and not taken from the access resolver.
   */
  caption: string
  retry: () => void
}

const DEFAULT: CapabilitiesContextValue = {
  capabilities: null,
  loading: true,
  canView: false,
  canEditFleet: false,
  canEditDrivers: false,
  canEditOwners: false,
  canViewFinance: false,
  canPushXero: false,
  isAdmin: false,
  canEditMarketing: false,
  canApproveContent: false,
  isDesigner: false,
  canCommentOnPipeline: false,
  degraded: false,
  caption: '',
  retry: () => {},
}

const CapabilitiesContext = createContext<CapabilitiesContextValue>(DEFAULT)

export function CapabilitiesProvider({ children }: { children: ReactNode }) {
  const { data, loading, trigger } = useGetCapabilities()

  useEffect(() => {
    trigger({})
  }, [trigger])

  // A DEGRADED REPLY MUST NEVER OVERWRITE A GOOD ONE.
  //
  // The defect this fixes, reported from the screen on 26 August 2026. Luyanda,
  // who is admin, completed about five tasks in a row and the board replaced
  // itself with "The task board is available to signed-in Fleetly staff. Ask an
  // admin to add you to cockpit access."
  //
  // resolveCapabilities is careful and correct: on a transient database failure
  // it returns every flag false with status 'degraded', documented as "retryable;
  // never a denial the caller should treat as final". The status is right there
  // in the object. Nothing reads it. Fourteen pages read the FLAGS instead, and
  // `if (!canView) return <ask an admin>` cannot tell "you have no access" from
  // "we could not ask". So one blip in one query locks an admin out of his own
  // task board and tells him to go and find an admin.
  //
  // Fixing it in fourteen pages would be fourteen chances to get it wrong and a
  // fifteenth page tomorrow that forgets. It is fixed once, here, because this
  // is the only place the flags are derived: the last answer that actually
  // resolved stays in force, and a degraded reply raises the banner without
  // taking anybody's access away. Only a degraded reply arriving with NO good
  // answer behind it is allowed to gate a page, which is the genuine
  // "we have never been able to check" case.
  //
  // This is the same reasoning as the `loading` comment below, applied to the
  // failure path rather than the pending one: who can do what changes when a row
  // in cockpit_access changes, which is roughly never. It does not change because
  // a query timed out.
  const lastGood = useRef<Capabilities | null>(null)

  const value = useMemo<CapabilitiesContextValue>(() => {
    const incoming = (data as Capabilities | null | undefined) ?? null
    if (incoming !== null && incoming.status !== 'degraded') {
      lastGood.current = incoming
    }
    const degraded = incoming?.status === 'degraded'
    // Flags come from the last good answer; the degraded flag comes from the
    // reply in hand. Both are true at once, and the screen shows the banner over
    // real data instead of a lockout over no data.
    const capabilities = degraded ? (lastGood.current ?? incoming) : incoming
    return {
      capabilities,
      // LOADING MEANS "NOT KNOWN YET", NEVER "BEING RE-CHECKED".
      //
      // Every page in the cockpit does `if (accessLoading) return <Spinner/>`,
      // which unmounts the entire page. Since 22 August every successful write
      // has refetched every mounted read, and this is one of them, so a single
      // status change tore the whole screen down and rebuilt it: the board
      // vanished behind "Checking access...", every filter, every open panel
      // and the scroll position went with it, and the operator saw a full page
      // reload in the middle of a click they had just made.
      //
      // Who can do what does not change between one click and the next. It
      // changes when a row in cockpit_access changes, which is roughly never.
      // So the answer already in hand stays on screen while it is re-checked,
      // and only the very first resolve is allowed to blank a page.
      loading: loading && capabilities === null && lastGood.current === null,
      // Until capabilities resolve, default to no-access so edit affordances
      // never flash before the check completes.
      canView: capabilities?.canView === true,
      canEditFleet: capabilities?.canEditFleet === true,
      canEditDrivers: capabilities?.canEditDrivers === true,
      canEditOwners: capabilities?.canEditOwners === true,
      canViewFinance: capabilities?.canViewFinance === true,
      canPushXero: capabilities?.canPushXero === true,
      isAdmin: capabilities?.isAdmin === true,
      canEditMarketing: capabilities?.canEditMarketing === true,
      canApproveContent: capabilities?.canApproveContent === true,
      isDesigner: capabilities?.isDesigner === true,
      canCommentOnPipeline: capabilities?.canCommentOnPipeline === true,
      degraded,
      // The caption reads the retained capabilities so it keeps saying "Admin view"
      // rather than flipping to a permissions sentence while the banner is up. The
      // banner is what tells the operator something is uncertain.
      caption: capabilityCaption(capabilities),
      retry: () => trigger({}),
    }
  }, [data, loading, trigger])

  // A DEGRADED ANSWER RE-ASKS ITSELF, TWICE, THEN STOPS.
  //
  // The banner is honest about the reply it has and silent about how old that
  // reply is. getCapabilities sits out the write refresh wave on purpose, so
  // without this the banner stands until the operator comes back to the window
  // or presses Retry, and reloading the page is the only reliable cure. That is
  // the complaint this is answering. The schedule is in degradedRetry.ts.
  const degradedAttempts = useRef(0)
  useEffect(() => {
    if (!value.degraded) {
      degradedAttempts.current = 0
      return
    }
    const delay = nextDegradedDelay(degradedAttempts.current)
    if (delay === null) return
    const timer = setTimeout(() => {
      degradedAttempts.current += 1
      trigger({})
    }, delay)
    return () => clearTimeout(timer)
  }, [value.degraded, data, trigger])

  return <CapabilitiesContext.Provider value={value}>{children}</CapabilitiesContext.Provider>
}

export function useCapabilities() {
  return useContext(CapabilitiesContext)
}
